import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MapPin, Clock } from "lucide-react";
import Seo from "../components/site/Seo";
import { Container, Eyebrow } from "../components/site/Ui";
import { api, errMessage } from "../lib/api";
import PrivacyNote from "../components/site/PrivacyNote";
import { COMPANY } from "../data/legal";
import { useLang } from "../lib/LangContext";


const topics = ["Sales", "Account", "Billing", "Content", "Technical Issue", "Other"];

export default function Contact() {
  const { tr } = useLang();
  const [form, setForm] = useState({ name: "", business_name: "", email: "", topic: "Sales", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = tr("Please enter your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = tr("Please enter a valid email address.");
    if (form.message.trim().length < 10) next.message = tr("Please tell us a little more (at least 10 characters).");
    setErrors(next);
    if (Object.keys(next).length) return;

    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setSent(true);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setSending(false);
    }
  };

  const field = "w-full rounded-xl border border-slate-300 px-4 py-3 text-base text-slate-900 outline-none transition-colors focus:border-[#F95738] focus:ring-2 focus:ring-[#F95738]/20";

  return (
    <>
      <Seo
        title="Contact — talk to PostMint about your social media content"
        description="Contact PostMint about social media content for your business. Tell us about your company and we will reply with the best fit for your needs."
        path="/contact"
      />

      <section className="mesh-warm" data-testid="contact-hero">
        <Container className="py-16 lg:py-20">
          <Eyebrow>{tr("Contact")}</Eyebrow>
          <h1 className="mt-5 max-w-3xl text-4xl font-extrabold leading-tight text-slate-900 sm:text-5xl">
            {tr("Tell us about your business.")}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            {tr("Send us a message and we'll get back to you. Response times may vary depending on demand and package. We do not offer 24/7 support.")}
          </p>
        </Container>
      </section>

      <section className="py-16">
        <Container className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr]">
          {sent ? (
            <div
              data-testid="contact-success"
              className="rounded-3xl border border-slate-200 bg-white p-8 soft-shadow"
            >
              <h2 className="font-display text-2xl font-bold text-slate-900">{tr("Message sent")}</h2>
              <p className="mt-3 text-sm text-slate-600">
                {tr("Thanks for reaching out. We'll come back to you as soon as we can.")}
              </p>
            </div>
          ) : (
            <form
              onSubmit={submit}
              noValidate
              data-testid="contact-form"
              className="rounded-3xl border border-slate-200 bg-white p-6 soft-shadow sm:p-8"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="c-name" className="text-sm font-semibold text-slate-800">
                    {tr("Name *")}
                  </label>
                  <input id="c-name" data-testid="contact-name-input" className={`mt-2 ${field}`} value={form.name} onChange={set("name")} />
                  {errors.name && <p data-testid="contact-name-error" className="mt-1.5 text-sm text-red-600">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="c-business" className="text-sm font-semibold text-slate-800">
                    {tr("Business Name")}
                  </label>
                  <input id="c-business" data-testid="contact-business-input" className={`mt-2 ${field}`} value={form.business_name} onChange={set("business_name")} />
                </div>
                <div>
                  <label htmlFor="c-email" className="text-sm font-semibold text-slate-800">
                    {tr("Email *")}
                  </label>
                  <input id="c-email" type="email" data-testid="contact-email-input" className={`mt-2 ${field}`} value={form.email} onChange={set("email")} />
                  {errors.email && <p data-testid="contact-email-error" className="mt-1.5 text-sm text-red-600">{errors.email}</p>}
                </div>
                <div>
                  <label htmlFor="c-topic" className="text-sm font-semibold text-slate-800">
                    {tr("Topic")}
                  </label>
                  <select id="c-topic" data-testid="contact-topic-select" className={`mt-2 ${field}`} value={form.topic} onChange={set("topic")}>
                    {topics.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="c-message" className="text-sm font-semibold text-slate-800">
                  {tr("Message *")}
                </label>
                <textarea id="c-message" rows={6} data-testid="contact-message-input" className={`mt-2 ${field}`} value={form.message} onChange={set("message")} />
                {errors.message && <p data-testid="contact-message-error" className="mt-1.5 text-sm text-red-600">{errors.message}</p>}
              </div>

              <PrivacyNote
                className="mt-4"
                purpose="We use your name, email and message only to answer you and to keep a record of the conversation."
                testId="contact-privacy-note"
              />

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="mt-6 w-full rounded-full bg-[#F95738] px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#E0482B] disabled:opacity-60 sm:w-auto"
              >
                {sending ? tr("Sending…") : tr("Send Message")}
              </button>
            </form>
          )}

          <aside className="space-y-4">
            {[
              [Mail, "Email", COMPANY.email],
              [MapPin, "Based in", "The Netherlands"],
              [Clock, "Support", "Response times vary by demand and package."],
            ].map(([Icon, label, value]) => (
              <div key={label} className="rounded-2xl border border-slate-200 bg-white p-6 soft-shadow">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFF3EF] text-[#F95738]">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-400">{tr(label)}</p>
                <p className="text-sm text-slate-800">{tr(value)}</p>
              </div>
            ))}
          </aside>
        </Container>
      </section>

      <section className="pb-20" data-testid="contact-complaints">
        <Container>
          <div className="rounded-3xl border border-slate-200 bg-white p-6 soft-shadow sm:p-8">
            <h2 className="font-display text-xl font-semibold text-slate-900">{tr("Complaints")}</h2>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">
              {tr("If something has gone wrong with the service, your content or your billing, email")}{" "}
              <a href={`mailto:${COMPANY.email}`} className="font-semibold text-[#F95738] hover:underline">
                {COMPANY.email}
              </a>{" "}
              {tr("from the address on your account and tell us what happened, with the date and any invoice or ticket reference. We aim to acknowledge your complaint promptly and to explain the outcome in writing. Billing complaints are handled by the same address — you do not need to call anyone.")}
            </p>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">
              {tr("If you are not satisfied with our response, you can escalate to the competent Dutch consumer authority, or — for privacy matters — to the Dutch Data Protection Authority (Autoriteit Persoonsgegevens) or the supervisory authority in your own country.")}
            </p>
            <p className="mt-4 text-sm">
              <Link to="/legal/legal-notice" data-testid="contact-legal-notice-link" className="font-semibold text-[#F95738] hover:underline">
                {tr("Company details & legal notice")}
              </Link>
            </p>
          </div>
        </Container>
      </section>
    </>
  );
}
